import * as React from 'react'
import isNil from 'lodash.isnil'

import { MaybeRenderProp, createContext } from '@clara/react-utils'
import { runIfFn } from '@clara/utils'
import { useAuthContext } from '@clara/auth'
import { useGetTabsCount } from '@/features/reimbursements/api/get-tabs-count'
import { SharedDbProvider } from '@/features/payments/use-shared-db'
import { captureSentryException } from '@/utils/sentry'

import {
  useGetBankInfo,
  useGetUserInfo,
} from '../../features/reimbursements/api/bank-info'

const [GlobalStateContextProvider, useGlobalStateContext] =
  createContext<GlobalStateContext>({
    name: 'GlobalStateContext',
    strict: true,
  })

export { useGlobalStateContext }

function useGlobalState() {
  const [bankInfoResponse, getBankInfo] = useGetBankInfo()
  const [userInfoResponse, getUserInfo] = useGetUserInfo()
  const [tabsCountResponse, getTabsCount] = useGetTabsCount()
  const [tabCount, setTabCount] = React.useState({
    overview: 0,
    pendingApproval: 0,
    pendingPayment: 0,
  })
  const { user } = useAuthContext()

  const isCompanyOwner = user?.role === 'COMPANYOWNER'
  const isEmployee = user?.role === 'EMPLOYEE'

  const reimbursement = user?.__products__?.byName['REIMBURSEMENT'] // product with id 15
  const approvalFlows = user?.__products__?.byName['APPROVAL_FLOWS'] // product with id 21

  const canApprove = approvalFlows?.userMetadata?.isApprover ?? false
  const canPay = reimbursement?.userMetadata?.manager ?? false

  const isManagerOrCompanyOwner = canPay || isCompanyOwner

  const hasAccessToPayments = user?.can_make_payments

  React.useEffect(() => {
    const { overview, pendingApproval, pendingPayment } =
      tabsCountResponse.value ?? {}
    setTabCount((prev) => {
      if (isNil(overview) || isNil(pendingApproval) || isNil(pendingPayment)) {
        return prev
      }
      return {
        overview,
        pendingApproval,
        pendingPayment,
      }
    })
  }, [setTabCount, tabsCountResponse.value])

  React.useEffect(() => {
    if (
      !userInfoResponse.loading &&
      !userInfoResponse.value &&
      userInfoResponse.error
    ) {
      captureSentryException(
        new Error('User without userInfo', { cause: userInfoResponse.error }),
        {
          tags: {
            team: 'reimbursements',
          },
          contexts: {
            user: {
              userId: user?.uuid,
              email: user?.email,
              role: user?.role,
              canApprove,
              canPay,
            },
          },
        },
      )
    }
  }, [userInfoResponse, user, canApprove, canPay])

  return {
    bankInfo: bankInfoResponse.value,
    bankInfoIsLoading: bankInfoResponse.loading,
    getBankInfo,
    isCompanyOwner,
    userAndCompanyHavePermissions: !!reimbursement,
    canApprove,
    canPay,
    getUserInfo,
    userInfoResponse,
    isManagerOrCompanyOwner,
    tabCount,
    setTabCount,
    getTabsCount,
    hasAccessToPayments,
    isEmployee,
  }
}

type GlobalStateContext = ReturnType<typeof useGlobalState>

export interface GlobalStateProviderProps {
  children?: MaybeRenderProp<GlobalStateContext>
}

export function GlobalStateProvider(props: GlobalStateProviderProps) {
  const ctx = useGlobalState()
  const context = React.useMemo(() => ctx, [ctx])

  const children = runIfFn(props.children, context)

  return (
    <SharedDbProvider>
      <GlobalStateContextProvider value={context}>
        {children}
      </GlobalStateContextProvider>
    </SharedDbProvider>
  )
}
